import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const SERVICES = [
  {
    n: "01",
    title: "Airport Transfers",
    desc: "Flight-aware pickups at Sydney Airport terminals, meet & greet on arrival, and unhurried departures.",
  },
  {
    n: "02",
    title: "Corporate Travel",
    desc: "Discreet executive transport, meetings and roadshows — with consolidated account invoicing.",
  },
  {
    n: "03",
    title: "Events & Occasions",
    desc: "Weddings, galas and private events. Multi-vehicle logistics handled with care.",
  },
  {
    n: "04",
    title: "Hourly Hire",
    desc: "A dedicated chauffeur for the day — meetings, appointments, dinner reservations, or leisure.",
  },
  {
    n: "05",
    title: "Hotel Transfers",
    desc: "Concierge-level pick-ups from five-star hotels and private residences across Sydney.",
  },
  {
    n: "06",
    title: "Long-Distance",
    desc: "Regional NSW travel — Hunter Valley, Southern Highlands, Blue Mountains — in composed comfort.",
  },
];

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function SignatureServices() {
  return (
    <section
      data-testid="signature-services-section"
      className="bg-warm-white text-charcoal py-24 md:py-32 border-t border-charcoal/10"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24"
        >
          <div className="max-w-2xl">
            <p className="sjc-eyebrow text-muted-light flex items-center gap-4">
              <span className="sjc-hairline w-8" />
              Signature Services
            </p>
            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em]">
              One private chauffeur experience,
              <br className="hidden md:block" />
              <span className="italic font-light"> shaped to your itinerary.</span>
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="signature-services-view-all"
            className="group inline-flex items-center gap-3 text-charcoal/80 hover:text-charcoal text-[11px] tracking-[0.28em] uppercase font-medium transition-colors duration-300 self-start"
          >
            <span>All Services</span>
            <span className="w-10 h-px bg-gold transition-all duration-500 group-hover:w-14" />
          </Link>
        </motion.div>

        <motion.ul
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          {SERVICES.map((s, i) => (
            <motion.li
              key={s.n}
              variants={item}
              data-testid={`service-card-${s.n}`}
              className={`group relative px-2 py-10 md:p-10 lg:p-12 border-t border-charcoal/10 transition-colors duration-500 hover:bg-ivory ${
                // Right-hand borders on desktop 3-col; center only for middle col.
                i % 3 !== 2 ? "lg:border-r" : ""
              } ${i % 2 !== 1 ? "md:border-r lg:border-r" : ""} ${
                // Reset md right-border alignment for full 3-col layout
                "md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(3n)]:border-r-0 md:[&:nth-child(3n+1)]:md:border-r"
              } border-charcoal/10`}
            >
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-display text-gold/80 text-2xl italic">
                  {s.n}
                </span>
                <span className="w-10 h-px bg-charcoal/10 group-hover:bg-gold/60 transition-colors duration-500" />
              </div>
              <h3 className="font-display text-2xl md:text-[28px] leading-tight tracking-tight text-charcoal">
                {s.title}
              </h3>
              <p className="mt-5 text-muted-light text-sm md:text-[15px] leading-relaxed font-light max-w-sm">
                {s.desc}
              </p>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
